import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PriceInput } from '@/components/ui/price-input'
import {
  EXPENSE_CATEGORIES,
  INCOME_CATEGORIES,
  type Transaction,
  type TransactionCategory,
  type TransactionType,
  type TransactionWriteBody,
} from '@/types/finance'
interface TransactionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing?: Transaction | null
  onSubmit: (body: TransactionWriteBody) => void
  pending: boolean
}

const categoriesFor = (type: TransactionType) =>
  type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES

export function TransactionDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
  pending,
}: TransactionDialogProps) {
  const today = new Date().toISOString().slice(0, 10)
  const [type, setType] = useState<TransactionType>(editing?.type ?? 'expense')
  const [category, setCategory] = useState<TransactionCategory | ''>(editing?.category ?? '')
  const [occurredOn, setOccurredOn] = useState(editing?.occurred_on ?? today)
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '')
  const [description, setDescription] = useState(editing?.description ?? '')

  const categories = categoriesFor(type)
  const amountNum = Number(amount)

  const submit = () => {
    if (!category || !occurredOn || !(amountNum > 0)) return
    onSubmit({
      occurred_on: occurredOn,
      type,
      category: category as TransactionCategory,
      amount: amountNum,
      description: description.trim(),
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setType(editing?.type ?? 'expense')
          setCategory(editing?.category ?? '')
          setOccurredOn(editing?.occurred_on ?? today)
          setAmount(editing ? String(editing.amount) : '')
          setDescription(editing?.description ?? '')
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit ledger entry' : 'Add ledger entry'}</DialogTitle>
          <DialogDescription>
            {editing?.source && editing.source !== 'manual'
              ? `Auto-recorded from ${editing.source.replace('_', ' ')} — edits stick until the source changes again.`
              : 'Record income or an expense on a specific date.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={type}
                onValueChange={(v) => {
                  setType(v as TransactionType)
                  setCategory('')
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as TransactionCategory)}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick…" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="tx-date">Date</Label>
              <Input
                id="tx-date"
                type="date"
                value={occurredOn}
                onChange={(e) => setOccurredOn(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tx-amount">Amount ($)</Label>
              <PriceInput id="tx-amount" value={amount} onChange={setAmount} onBlur={() => {}} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tx-desc">Description</Label>
            <Input
              id="tx-desc"
              placeholder="e.g. Diesel delivery — dock A"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !category || !(amountNum > 0) || !occurredOn}>
            {pending ? 'Saving…' : editing ? 'Save changes' : 'Add entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
